import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Send, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ENQUIRY_OPTIONS } from "@/data/site";

const WHATSAPP_NUMBER = "447984341251"; // 07984 341251 in international format
const CONTACT_EMAIL = "Info@nurtherapy.co.uk";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", treatment: "", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMessage = () =>
    `Hi Nur Therapy,\n\nMy name is ${form.name}. I'd like to know more about: ${form.treatment}.` +
    (form.message ? `\n\n${form.message}` : "") +
    `\n\nThank you.`;

  const validate = () => {
    if (!form.name.trim()) {
      toast.error("Please add your name.");
      return false;
    }
    if (!form.treatment) {
      toast.error("Please choose a treatment you're interested in.");
      return false;
    }
    return true;
  };

  const sendWhatsApp = () => {
    if (!validate()) return;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildMessage())}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your enquiry…");
  };

  const sendEmail = () => {
    if (!validate()) return;
    const subject = `Treatment Enquiry — ${form.treatment}`;
    const url = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildMessage())}`;
    window.location.href = url;
    toast.success("Opening your email with the enquiry ready to send…");
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-forest px-6 py-28 text-cream md:px-12 md:py-40"
    >
      <div className="mx-auto grid max-w-[1400px] gap-16 lg:grid-cols-2 lg:gap-24">
        {/* Left — invitation + details */}
        <div>
          <Reveal>
            <p className="mb-6 text-[0.7rem] uppercase tracking-[0.3em] text-sage">
              Get in touch
            </p>
            <h2 className="font-serif text-4xl leading-tight tracking-tight sm:text-5xl md:text-6xl">
              Begin your
              <br />
              <span className="italic text-sunlight">journey to light</span>
            </h2>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-cream/80">
              Choose the treatment you're curious about and send us a quick message by
              WhatsApp or email — we'll get straight back to you personally.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 space-y-6">
              <div className="flex flex-col gap-3">
                <span className="text-[0.7rem] uppercase tracking-[0.25em] text-cream/60">
                  Call or message us · 07984 341251
                </span>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="tel:+447984341251"
                    data-testid="contact-call-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 text-sm tracking-wide text-forest transition-colors duration-300 hover:bg-sunlight"
                  >
                    <Phone size={16} /> Call
                  </a>
                  <a
                    href="https://wa.me/447984341251"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-6 py-3 text-sm tracking-wide text-cream transition-colors duration-300 hover:border-sunlight hover:text-sunlight"
                  >
                    <MessageCircle size={16} /> Send Message
                  </a>
                </div>
              </div>
              <a
                href="mailto:Info@nurtherapy.co.uk"
                data-testid="contact-email"
                className="group flex items-center gap-4 text-cream/90 transition-colors hover:text-sunlight"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/25 transition-colors group-hover:border-sunlight">
                  <Mail size={18} />
                </span>
                Info@nurtherapy.co.uk
              </a>
              <div className="flex items-center gap-4 text-cream/90">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/25">
                  <MapPin size={18} />
                </span>
                Woodford Green · Epping Forest, Essex
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right — form */}
        <Reveal delay={0.1}>
          <form
            data-testid="contact-form"
            onSubmit={(e) => e.preventDefault()}
            className="flex flex-col gap-7"
          >
            <div className="flex flex-col gap-2">
              <label className="text-[0.7rem] uppercase tracking-[0.25em] text-cream/60">
                Your name
              </label>
              <input
                data-testid="contact-name-input"
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Doe"
                className="border-0 border-b border-cream/25 bg-transparent pb-3 text-lg text-cream placeholder:text-cream/40 focus:border-sunlight focus:outline-none focus:ring-0 transition-colors"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[0.7rem] uppercase tracking-[0.25em] text-cream/60">
                Treatment you're interested in
              </label>
              <select
                data-testid="contact-treatment-select"
                value={form.treatment}
                onChange={update("treatment")}
                className="cursor-pointer border-0 border-b border-cream/25 bg-transparent pb-3 text-lg text-cream focus:border-sunlight focus:outline-none focus:ring-0 transition-colors [&>option]:text-ink"
              >
                <option value="" disabled>
                  Select a treatment…
                </option>
                {ENQUIRY_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[0.7rem] uppercase tracking-[0.25em] text-cream/60">
                Anything else? (optional)
              </label>
              <textarea
                data-testid="contact-message-input"
                rows={3}
                value={form.message}
                onChange={update("message")}
                placeholder="A little about what you're looking for..."
                className="resize-none border-0 border-b border-cream/25 bg-transparent pb-3 text-lg text-cream placeholder:text-cream/40 focus:border-sunlight focus:outline-none focus:ring-0 transition-colors"
              />
            </div>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row">
              <button
                type="button"
                data-testid="contact-whatsapp-btn"
                onClick={sendWhatsApp}
                className="inline-flex items-center justify-center gap-3 rounded-full bg-cream px-8 py-4 text-sm tracking-wide text-forest transition-colors duration-300 hover:bg-sunlight"
              >
                <MessageCircle size={18} /> Enquire via WhatsApp
              </button>
              <button
                type="button"
                data-testid="contact-email-btn"
                onClick={sendEmail}
                className="inline-flex items-center justify-center gap-3 rounded-full border border-cream/40 px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:border-sunlight hover:text-sunlight"
              >
                <Send size={18} /> Enquire via Email
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
